import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto'

import { Container, LoadingButton } from '../../../components'
import {
    AssetCondition,
    formatConditionLabel,
    formatStatusLabel,
    getConditionColor,
    getInventoryConfigOptions,
    getPhotoUrl,
    getStatusColor,
    InventoryAsset,
    InventoryAssetPatchPayload,
    InventoryAssetStatus,
    InventoryConfigOption,
    PartialAttachPhotosError,
    setPrimaryInventoryPhoto,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'
import { AssetProductDocuments } from './AssetProductDocuments'

const MAX_PHOTO_SIZE_BYTES = 15 * 1024 * 1024

const STATUS_OPTIONS: InventoryAssetStatus[] = [
    'active',
    'maintenance',
    'pending_replacement',
    'replaced',
    'removed',
]

const CONDITION_OPTIONS: AssetCondition[] = ['new', 'good', 'fair', 'poor']

interface Props {
    open: boolean
    asset: InventoryAsset | null
    isPendingReview?: boolean
    onClose: () => void
    onSave: (
        assetId: number,
        payload: InventoryAssetPatchPayload,
        pendingPhotoFiles: File[],
    ) => Promise<InventoryAsset>
}

export const AssetEditModal: React.FC<Props> = ({
    open,
    asset,
    isPendingReview = false,
    onClose,
    onSave,
}) => {
    const theme = useTheme()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [name, setName] = useState('')
    const [quantity, setQuantity] = useState('1')
    const [condition, setCondition] = useState<AssetCondition>('good')
    const [conditionNotes, setConditionNotes] = useState('')
    const [brand, setBrand] = useState('')
    const [modelNumber, setModelNumber] = useState('')
    const [serialNumber, setSerialNumber] = useState('')
    const [manufactureYear, setManufactureYear] = useState('')
    const [color, setColor] = useState('')
    const [specs, setSpecs] = useState('')
    const [status, setStatus] = useState<InventoryAssetStatus>('active')
    const [pendingPhotos, setPendingPhotos] = useState<File[]>([])
    const [pendingPhotoPreviews, setPendingPhotoPreviews] = useState<string[]>(
        [],
    )
    const [saving, setSaving] = useState(false)
    const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(
        null,
    )
    const [displayAsset, setDisplayAsset] = useState<InventoryAsset | null>(
        null,
    )
    const [inventoryConfigId, setInventoryConfigId] = useState<number | null>(
        null,
    )
    const [configOptions, setConfigOptions] = useState<InventoryConfigOption[]>(
        [],
    )
    const [loadingConfigOptions, setLoadingConfigOptions] = useState(false)

    useEffect(() => {
        if (!asset) {
            return
        }

        setDisplayAsset(asset)
        setName(asset.name)
        setQuantity(String(asset.quantity))
        setCondition(asset.condition)
        setConditionNotes(asset.condition_notes ?? '')
        setBrand(asset.brand ?? '')
        setModelNumber(asset.model_number ?? '')
        setSerialNumber(asset.serial_number ?? '')
        setManufactureYear(
            asset.manufacture_year ? String(asset.manufacture_year) : '',
        )
        setColor(asset.color ?? '')
        setSpecs(asset.specs ?? '')
        setStatus(asset.status)
        setInventoryConfigId(asset.inventory_config_id ?? null)
        setPendingPhotos([])
        setPendingPhotoPreviews([])

        setLoadingConfigOptions(true)
        getInventoryConfigOptions(asset.id)
            .then((res) => setConfigOptions(res.data.options))
            .catch(() => setConfigOptions([]))
            .finally(() => setLoadingConfigOptions(false))
    }, [asset, open])

    useEffect(() => {
        return () => {
            pendingPhotoPreviews.forEach((url) => URL.revokeObjectURL(url))
        }
    }, [pendingPhotoPreviews])

    const handleClose = () => {
        pendingPhotoPreviews.forEach((url) => URL.revokeObjectURL(url))
        onClose()
    }

    const handleSelectPhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        if (files.length === 0) {
            return
        }

        const validFiles: File[] = []
        for (const file of files) {
            if (file.size > MAX_PHOTO_SIZE_BYTES) {
                toast.error(`${file.name} must be 15 MB or smaller`)
                continue
            }
            if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
                toast.error(`${file.name} must be a JPEG or PNG image`)
                continue
            }
            validFiles.push(file)
        }

        if (validFiles.length === 0) {
            return
        }

        setPendingPhotos((prev) => [...prev, ...validFiles])
        setPendingPhotoPreviews((prev) => [
            ...prev,
            ...validFiles.map((file) => URL.createObjectURL(file)),
        ])

        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleSetPrimary = async (photoId: number) => {
        if (!displayAsset) {
            return
        }

        setSettingPrimaryId(photoId)
        try {
            const response = await setPrimaryInventoryPhoto(
                displayAsset.id,
                photoId,
            )
            setDisplayAsset(response.data)
            toast.success('Primary photo updated')
        } catch (error) {
            console.error(error)
            toast.error('Could not set primary photo')
        } finally {
            setSettingPrimaryId(null)
        }
    }

    const handleSave = async () => {
        if (!displayAsset) {
            return
        }

        const parsedQuantity = Number(quantity)
        if (!name.trim()) {
            toast.error('Name is required')
            return
        }
        if (!Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
            toast.error('Quantity must be at least 1')
            return
        }

        let parsedYear: number | null = null
        if (manufactureYear) {
            parsedYear = Number(manufactureYear)
            if (
                !Number.isFinite(parsedYear) ||
                parsedYear < 1900 ||
                parsedYear > new Date().getFullYear() + 1
            ) {
                toast.error('Enter a valid manufacture year')
                return
            }
        }

        const payload: InventoryAssetPatchPayload = {
            name: name.trim(),
            quantity: parsedQuantity,
            condition,
            condition_notes: conditionNotes.trim() || null,
            brand: brand.trim() || null,
            model_number: modelNumber.trim() || null,
            serial_number: serialNumber.trim() || null,
            manufacture_year: parsedYear,
            color: color.trim() || null,
            specs: specs.trim() || null,
            status,
            inventory_config_id: inventoryConfigId,
        }

        setSaving(true)
        try {
            const updatedAsset = await onSave(
                displayAsset.id,
                payload,
                pendingPhotos,
            )
            setDisplayAsset(updatedAsset)
            toast.success(
                isPendingReview ? 'Asset confirmed and saved' : 'Asset saved',
            )
            handleClose()
        } catch (error) {
            if (error instanceof PartialAttachPhotosError) {
                setDisplayAsset(error.asset)
                toast.warn(
                    `Saved, but some photos failed: ${error.errors.join(', ')}`,
                )
                handleClose()
                return
            }
            console.error(error)
            toast.error('Could not save asset')
        } finally {
            setSaving(false)
        }
    }

    if (!displayAsset) {
        return null
    }

    const existingPhotos = displayAsset.photos ?? []

    const sectionTitleStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle2),
        fontWeight: 700,
        color: theme.palette.text.primary,
        letterSpacing: '0.02em',
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ style: { borderRadius: 16 } }}
        >
            <DialogTitle style={{ padding: theme.spacing(2, 3) }}>
                <Container
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            {isPendingReview ? 'Review asset' : 'Edit asset'}
                        </span>
                        {isPendingReview && (
                            <Chip
                                size="small"
                                label="Pending"
                                style={{
                                    backgroundColor:
                                        theme.palette.warning.light,
                                    color: theme.palette.warning.dark,
                                    fontWeight: 600,
                                    fontSize: 11,
                                }}
                            />
                        )}
                    </Container>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>
            <DialogContent dividers style={{ padding: theme.spacing(3) }}>
                <Container
                    style={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: theme.spacing(3),
                    }}
                >
                    {/* Photos section */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <Container
                            style={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span style={sectionTitleStyle}>Photos</span>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddAPhotoIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    textTransform: 'none',
                                    borderRadius: 8,
                                    fontWeight: 500,
                                }}
                            >
                                Add photos
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/jpg"
                                multiple
                                hidden
                                onChange={handleSelectPhotos}
                            />
                        </Container>
                        <Container
                            style={{
                                flexWrap: 'wrap',
                                gap: 10,
                            }}
                        >
                            {existingPhotos.length === 0 &&
                                pendingPhotoPreviews.length === 0 && (
                                    <Container
                                        style={{
                                            width: '100%',
                                            height: 100,
                                            borderRadius: 12,
                                            border: `2px dashed ${theme.palette.grey[300]}`,
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            gap: 4,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                    >
                                        <PhotoCameraIcon
                                            style={{
                                                color: theme.palette.grey[400],
                                                fontSize: 28,
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color:
                                                    theme.palette.text.disabled,
                                            }}
                                        >
                                            Click to add photos
                                        </span>
                                    </Container>
                                )}
                            {existingPhotos.map((photo) => (
                                <Container
                                    key={photo.id}
                                    style={{
                                        position: 'relative',
                                        width: 100,
                                        height: 100,
                                        borderRadius: 10,
                                        overflow: 'hidden',
                                        border: photo.is_primary
                                            ? `2px solid ${theme.palette.primary.main}`
                                            : `1px solid ${theme.palette.grey[200]}`,
                                        boxShadow: photo.is_primary
                                            ? `0 0 0 2px ${theme.palette.primary.main}30`
                                            : 'none',
                                    }}
                                >
                                    <img
                                        src={getPhotoUrl(photo)}
                                        alt=""
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Tooltip
                                        title={
                                            photo.is_primary
                                                ? 'Primary photo'
                                                : 'Set as primary'
                                        }
                                    >
                                        <IconButton
                                            size="small"
                                            disabled={
                                                settingPrimaryId === photo.id
                                            }
                                            onClick={() =>
                                                handleSetPrimary(photo.id)
                                            }
                                            style={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                backgroundColor:
                                                    'rgba(255,255,255,0.9)',
                                                padding: 4,
                                                boxShadow:
                                                    '0 1px 4px rgba(0,0,0,0.15)',
                                            }}
                                        >
                                            {photo.is_primary ? (
                                                <StarIcon
                                                    style={{
                                                        fontSize: 16,
                                                        color:
                                                            theme.palette
                                                                .primary.main,
                                                    }}
                                                />
                                            ) : (
                                                <StarBorderIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Container>
                            ))}
                            {pendingPhotoPreviews.map((preview, index) => (
                                <Container
                                    key={`pending-${index}`}
                                    style={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: 10,
                                        overflow: 'hidden',
                                        border: `2px dashed ${theme.palette.primary.main}60`,
                                        position: 'relative',
                                    }}
                                >
                                    <img
                                        src={preview}
                                        alt=""
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            opacity: 0.85,
                                        }}
                                    />
                                    <span
                                        style={{
                                            position: 'absolute',
                                            bottom: 4,
                                            left: 4,
                                            fontSize: 9,
                                            fontWeight: 600,
                                            backgroundColor:
                                                theme.palette.primary.main,
                                            color: '#fff',
                                            borderRadius: 4,
                                            padding: '1px 5px',
                                        }}
                                    >
                                        NEW
                                    </span>
                                </Container>
                            ))}
                        </Container>
                    </Container>

                    <Divider />

                    {/* Basic info section */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: theme.spacing(2),
                        }}
                    >
                        <span style={sectionTitleStyle}>Basic information</span>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    size="small"
                                    label="Name"
                                    value={name}
                                    onChange={(event) =>
                                        setName(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Category"
                                    value={displayAsset.category}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Quantity"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    value={quantity}
                                    onChange={(event) =>
                                        setQuantity(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Condition"
                                    value={condition}
                                    onChange={(event) =>
                                        setCondition(
                                            event.target
                                                .value as AssetCondition,
                                        )
                                    }
                                    SelectProps={{
                                        renderValue: (value) => (
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: getConditionColor(
                                                            value as AssetCondition,
                                                        ),
                                                    }}
                                                />
                                                {formatConditionLabel(
                                                    value as AssetCondition,
                                                )}
                                            </Container>
                                        ),
                                    }}
                                >
                                    {CONDITION_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: getConditionColor(
                                                            option,
                                                        ),
                                                    }}
                                                />
                                                {formatConditionLabel(option)}
                                            </Container>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Status"
                                    value={status}
                                    onChange={(event) =>
                                        setStatus(
                                            event.target
                                                .value as InventoryAssetStatus,
                                        )
                                    }
                                    SelectProps={{
                                        renderValue: (value) => (
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(
                                                            value as InventoryAssetStatus,
                                                        ),
                                                    }}
                                                />
                                                {formatStatusLabel(
                                                    value as InventoryAssetStatus,
                                                )}
                                            </Container>
                                        ),
                                    }}
                                >
                                    {STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            <Container
                                                style={{
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(
                                                            option,
                                                        ),
                                                    }}
                                                />
                                                {formatStatusLabel(option)}
                                            </Container>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Inventory type"
                                    value={
                                        inventoryConfigId === null
                                            ? '__custom__'
                                            : String(inventoryConfigId)
                                    }
                                    onChange={(event) => {
                                        const val = event.target.value
                                        setInventoryConfigId(
                                            val === '__custom__'
                                                ? null
                                                : Number(val),
                                        )
                                    }}
                                    disabled={loadingConfigOptions}
                                    InputProps={{
                                        endAdornment: loadingConfigOptions ? (
                                            <CircularProgress
                                                size={16}
                                                style={{ marginRight: 8 }}
                                            />
                                        ) : undefined,
                                    }}
                                >
                                    <MenuItem value="__custom__">
                                        Custom
                                    </MenuItem>
                                    {configOptions.map((opt) => (
                                        <MenuItem
                                            key={opt.inventory_config_id}
                                            value={String(
                                                opt.inventory_config_id,
                                            )}
                                        >
                                            {opt.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    variant="outlined"
                                    size="small"
                                    label="Condition notes"
                                    placeholder="Any damage, wear, or relevant notes..."
                                    value={conditionNotes}
                                    onChange={(event) =>
                                        setConditionNotes(event.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Container>

                    <Divider />

                    {/* Product details section */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: theme.spacing(2),
                        }}
                    >
                        <span style={sectionTitleStyle}>Product details</span>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Brand"
                                    value={brand}
                                    onChange={(event) =>
                                        setBrand(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Model number"
                                    value={modelNumber}
                                    onChange={(event) =>
                                        setModelNumber(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Serial number"
                                    value={serialNumber}
                                    onChange={(event) =>
                                        setSerialNumber(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Year"
                                    type="number"
                                    value={manufactureYear}
                                    onChange={(event) =>
                                        setManufactureYear(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Color"
                                    value={color}
                                    onChange={(event) =>
                                        setColor(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    variant="outlined"
                                    size="small"
                                    label="Specs"
                                    placeholder="Dimensions, capacity, features..."
                                    value={specs}
                                    onChange={(event) =>
                                        setSpecs(event.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Container>

                    {/* Product documents */}
                    <AssetProductDocuments
                        asset={displayAsset}
                        brand={brand}
                        modelNumber={modelNumber}
                        onAssetUpdated={setDisplayAsset}
                    />
                </Container>
            </DialogContent>
            <DialogActions
                style={{ padding: theme.spacing(2, 3), gap: theme.spacing(1) }}
            >
                <Button
                    onClick={handleClose}
                    style={{ textTransform: 'none', fontWeight: 500 }}
                >
                    Cancel
                </Button>
                <LoadingButton
                    loading={saving}
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 8,
                        padding: theme.spacing(1, 3),
                    }}
                >
                    {isPendingReview ? 'Confirm & save' : 'Save changes'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
